import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Divider,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
// Iconsax (2026-09-12, Rahim: "use a filled [tick] one with clicked selected ui
// and for the pen icon use the new one from our library") — app-wide icon
// standard per [[icon-library-iconsax]]. TickSquare/Bold is the exact icon+
// variant already used elsewhere in the app for a "this one is checked/
// selected" indicator (Reports' Customize Columns dialog) — reused here for
// consistency rather than picking a different tick shape. Iconsax icons don't
// accept sx — both are wrapped in a sized Box per that memory's fix pattern.
import { TickSquare, Edit2 } from "iconsax-react";
import { styled } from "@mui/material/styles";
import { toast } from "react-hot-toast";
import {
  fetchWorkspaces,
  switchWorkspace,
  setCurrentWorkspace,
  deleteWorkspace,
} from "../../redux/Slice/Workspace/workspaceSlice";
import WorkspaceDialog from "./WorkspaceDialog";
import { useSubscriptionGuard } from "../../hooks/useSubscriptionGuard";

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  color: theme.palette.text.primary,
  border: `1px solid ${
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.23)"
      : "rgba(0, 0, 0, 0.23)"
  }`,
  borderRadius: "8px",
  padding: { xs: "6px", sm: "6px 12px" },
  minWidth: { xs: "auto", sm: "auto" },
  "&:hover": {
    borderColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.4)"
        : "rgba(0, 0, 0, 0.4)",
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(0, 0, 0, 0.04)",
  },
}));

const WorkspaceSwitcher = ({ showTrigger = true }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const { workspaces, currentWorkspace, loading } = useSelector(
    (state) => state.workspace
  );
  const { userPermissions } = useSelector((state) => state.userDetails);
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create"); // 'create' or 'edit'
  const [editingWorkspace, setEditingWorkspace] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedWorkspaceForAction, setSelectedWorkspaceForAction] = useState(null);
  // 2026-09-12 (Rahim: "we select a workspace, for a few seconds nothing
  // happens i dont like that nothingness") — handleSwitchWorkspace does a
  // real network round-trip then a full page reload, with zero visual
  // feedback in between (only console.log, invisible to a real user). This
  // tracks which row was clicked so it can show a spinner immediately and
  // the others can be disabled against a second click mid-switch.
  const [switchingWorkspaceId, setSwitchingWorkspaceId] = useState(null);

  const { guard } = useSubscriptionGuard();

  // Check if user is owner (has Workspace.Delete permission)
  const isOwner = userPermissions?.permissions?.Workspace?.Delete === true;
  
  // Check if user has Workspace.View permission
  const canViewWorkspace = userPermissions?.permissions?.Workspace?.View === true;

  useEffect(() => {
    // Only fetch workspaces if user has View permission
    if (canViewWorkspace) {
      dispatch(fetchWorkspaces());
    }
  }, [dispatch, canViewWorkspace]);

  // Set current workspace from localStorage on mount
  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      const parsedUser = JSON.parse(user);
      if (parsedUser.workspaceId && workspaces.length > 0) {
        const workspace = workspaces.find(
          (w) => w._id === parsedUser.workspaceId
        );
        if (workspace) {
          dispatch(setCurrentWorkspace(workspace));
        }
      }
    }
  }, [workspaces, dispatch]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSwitchWorkspace = async (workspace) => {
    if (switchingWorkspaceId) return; // already switching — ignore a second click
    setSwitchingWorkspaceId(workspace._id);
    try {
      console.log('🔄 Attempting to switch to workspace:', workspace.name, workspace._id);
      const result = await dispatch(switchWorkspace(workspace._id)).unwrap();
      console.log('✅ Workspace switch successful:', result);

      dispatch(setCurrentWorkspace(workspace));
      handleClose();

      // Small delay to ensure all state updates are complete
      setTimeout(() => {
        if (typeof window !== "undefined" && window.location.pathname.includes("/chat/")) {
          const stripped = window.location.pathname.replace(/\/chat\/[^/]+$/, "");
          window.history.replaceState(
            window.history.state,
            "",
            `${stripped}${window.location.search}${window.location.hash}`
          );
        }
        // Reload page to update all components with new workspace
        // The tokenManager has been updated with the new token, so auth will work after reload
        console.log('🔄 Reloading page with new workspace...');
        window.location.reload();
      }, 100);
    } catch (error) {
      console.error("❌ Failed to switch workspace:", error);
      alert(`Failed to switch workspace: ${error || 'Unknown error'}`);
      setSwitchingWorkspaceId(null); // reset so the row isn't stuck spinning — success path skips this, page reloads instead
    }
  };

  const handleCreateWorkspace = async () => {
    if (!await guard(['workspacesCreated'])) return;
    setDialogMode("create");
    setEditingWorkspace(null);
    setDialogOpen(true);
    handleClose();
  };

  const handleEditWorkspace = async (workspace) => {
    if (!await guard([])) return;
    setDialogMode("edit");
    setEditingWorkspace(workspace);
    setDialogOpen(true);
    handleClose();
  };

  const handleDeleteWorkspace = async (workspace) => {
    if (!await guard([])) return;
    if (
      window.confirm(
        `Are you sure you want to delete "${workspace.name}"? This action cannot be undone.`
      )
    ) {
      try {
        await dispatch(deleteWorkspace(workspace._id)).unwrap();
        toast.success(`Workspace "${workspace.name}" deleted successfully`);
        dispatch(fetchWorkspaces());
        handleClose();
      } catch (error) {
        console.error("Failed to delete workspace:", error);
        alert(error || "Failed to delete workspace");
      }
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingWorkspace(null);
    dispatch(fetchWorkspaces());
  };


  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedWorkspaceForAction(null);
  };

  const handleEditFromActionMenu = () => {
    if (selectedWorkspaceForAction) {
      handleEditWorkspace(selectedWorkspaceForAction);
    }
    handleActionMenuClose();
  };

  const handleDeleteFromActionMenu = () => {
    if (selectedWorkspaceForAction) {
      handleDeleteWorkspace(selectedWorkspaceForAction);
    }
    handleActionMenuClose();
  };

  // Row-level styling below is gated on `!showTrigger` (2026-09-12, Rahim:
  // "update the ui for this use the same type of ui we are using overall the
  // inbox page — same font size padding hover etc"). `showTrigger={false}` is
  // only ever passed from the Workspace Settings modal (LeftNav.jsx) — the
  // quick-switch Menu elsewhere (showTrigger={true}) keeps its original MUI
  // MenuItem defaults untouched, byte-identical to before, same precedent as
  // DateLabel.jsx's variant prop (see [[reports-ui-polish-pass-2026-08-16]]):
  // restyle only the one caller that asked for it, not the shared default.
  const inModal = !showTrigger;
  const rowHoverSx = {
    borderRadius: "8px",
    "&:hover": {
      backgroundColor: (theme) =>
        theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)",
    },
  };

  const renderSwitcherContent = () => (
    <>
      <Box sx={{ p: inModal ? 0 : 1.5 }}>
        {/* In the modal, this label now lives in the Dialog's own header as a
            subtitle (LeftNav.jsx) — kept here, unchanged, for the quick-switch
            Menu (showTrigger={true}), which has no separate header of its own. */}
        {!inModal && (
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Switch Workspace
          </Typography>
        )}
        {workspaces.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No workspaces available
          </Typography>
        ) : (
          workspaces.map((workspace) => {
            const isSelected = currentWorkspace?._id === workspace._id;
            return (
            <MenuItem
              key={workspace._id}
              onClick={() => handleSwitchWorkspace(workspace)}
              // Disable every OTHER row while one is switching (2026-09-12,
              // Rahim: "for a few seconds nothing happens i dont like that
              // nothingness") — the clicked row itself stays enabled-looking,
              // it just swaps its icon for a spinner below, instead of
              // greying out along with the rest.
              disabled={Boolean(switchingWorkspaceId) && switchingWorkspaceId !== workspace._id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                ...(inModal
                  ? {
                      padding: "10px 12px",
                      margin: "5px 0",
                      ...rowHoverSx,
                      // "clicked/selected" ui (Rahim, 2026-09-12) — the current
                      // workspace gets its own tinted fill + accent border, not
                      // just a small floating tick, so it reads as an active
                      // selection at a glance rather than something you have to
                      // notice the tiny icon to figure out.
                      // Changed to grey, no colored border (Rahim, 2026-09-12:
                      // "i dont like the selected ui the green one ... make it
                      // grey with no coloured border just like we use in inbox
                      // all me unassigned etc") — exact same selected-row
                      // formula as InboxSettings.jsx's own All/Me/Active/
                      // Unassigned/Closed tabs, not a new one-off color.
                      ...(isSelected && {
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                        "&:hover": {
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.09)",
                        },
                      }),
                    }
                  : { py: 1.5 }),
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, fontSize: inModal ? "0.8125rem" : undefined }}
                >
                  {workspace.name}
                </Typography>
                {workspace.isDefault && (
                  <Chip
                    label="Default"
                    size="small"
                    sx={{ mt: 0.5, height: 20, fontSize: "0.65rem" }}
                  />
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {switchingWorkspaceId === workspace._id ? (
                  <CircularProgress size={16} sx={{ color: "primary.main", flexShrink: 0 }} />
                ) : (
                  isSelected && (
                    // Neutral, not green (same fix as the row background above) —
                    // matches InboxSettings.jsx's own selected-icon color exactly
                    // (theme.palette.text.primary, not a brand color).
                    <Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", color: "text.primary", flexShrink: 0 }}>
                      <TickSquare variant="Bold" size="100%" color="currentColor" />
                    </Box>
                  )
                )}
                {isOwner && !workspace.isDefault && (
                  <IconButton
                    size="small"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleEditWorkspace(workspace);
                    }}
                    sx={{
                      padding: "4px",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Edit2 size="100%" color="currentColor" />
                    </Box>
                  </IconButton>
                )}
              </Box>
            </MenuItem>
            );
          })
        )}
      </Box>

      {isOwner && (
        <Box>
          <Divider sx={inModal ? { my: 1.5, opacity: 0.5 } : undefined} />
          <Box sx={{ p: inModal ? 0 : 1, pt: inModal ? 1 : 1 }}>
            <MenuItem
              onClick={handleCreateWorkspace}
              disabled={workspaces.length >= 5}
              sx={inModal ? { padding: "10px 12px", ...rowHoverSx } : undefined}
            >
              <AddIcon sx={{ mr: 1.5, fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontSize: inModal ? "0.8125rem" : undefined }}>
                Create Workspace
              </Typography>
              {workspaces.length >= 5 && (
                <Typography variant="caption" sx={{ ml: "auto", color: "text.secondary" }}>
                  (Max 5)
                </Typography>
              )}
            </MenuItem>
          </Box>
        </Box>
      )}
    </>
  );

  // Hide workspace switcher completely if user doesn't have View permission
  if (!canViewWorkspace) {
    return null;
  }

  return (
    <>
      {showTrigger ? (
        <Box sx={{ display: "flex", alignItems: "center", marginRight: { xs: 0.5, sm: 2 } }}>
          {isXs ? (
          <Tooltip title={currentWorkspace?.name || "Select Workspace"}>
            <IconButton
              onClick={handleClick}
              disabled={loading}
              sx={(theme) => ({
                border: `1px solid ${
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.23)"
                    : "rgba(0, 0, 0, 0.23)"
                }`,
                borderRadius: "8px",
                padding: "6px",
                "&:hover": {
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.4)"
                      : "rgba(0, 0, 0, 0.4)",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.08)"
                      : "rgba(0, 0, 0, 0.04)",
                },
              })}
            >
              <BusinessIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
            </IconButton>
          </Tooltip>
        ) : (
          <StyledButton
            onClick={handleClick}
            startIcon={<BusinessIcon />}
            endIcon={<ArrowDropDownIcon />}
            disabled={loading}
          >
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <Typography variant="body2" sx={{ fontSize: "0.75rem", lineHeight: 1 }}>
                Workspace
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                {currentWorkspace?.name || "Select Workspace"}
              </Typography>
            </Box>
          </StyledButton>
        )}

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          PaperProps={{
            sx: {
              minWidth: 280,
              maxHeight: 400,
              mt: 1,
            },
          }}
        >
          {renderSwitcherContent()}
        </Menu>

        {/* Action Menu for Edit/Delete */}
        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={handleActionMenuClose}
          onClick={(e) => e.stopPropagation()}
        >
          <MenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleEditFromActionMenu();
            }}
          >
            <Box sx={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", mr: 1.5 }}>
              <Edit2 size="100%" color="currentColor" />
            </Box>
            <Typography variant="body2">Edit</Typography>
          </MenuItem>
          <MenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDeleteFromActionMenu();
            }}
            sx={{ color: "error.main" }}
          >
            <DeleteIcon sx={{ mr: 1.5, fontSize: 20 }} />
            <Typography variant="body2">Delete</Typography>
          </MenuItem>
        </Menu>
      </Box>
      ) : (
        <Box sx={{ minWidth: 320, maxHeight: 400, overflowY: "auto" }}>
          {renderSwitcherContent()}
        </Box>
      )}

      <WorkspaceDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        mode={dialogMode}
        workspace={editingWorkspace}
      />
    </>
  );
};

export default WorkspaceSwitcher;
